import type { ResponderMessageAsset } from './shared-responder-v2-core-gateway.types';

export const VOICE_RECORDING_STATUSES = {
  Idle: 'idle',
  Requesting: 'requesting',
  Listening: 'listening',
  Denied: 'denied',
  Unsupported: 'unsupported',
  Failed: 'failed',
} as const;

export type VoiceRecordingStatus =
  (typeof VOICE_RECORDING_STATUSES)[keyof typeof VOICE_RECORDING_STATUSES];

export const VOICE_RECORDING_FAILURE_REASONS = {
  PermissionDenied: 'permissionDenied',
  Unsupported: 'unsupported',
  DeviceLost: 'deviceLost',
  UploadFailed: 'uploadFailed',
} as const;

export type VoiceRecordingFailureReason =
  (typeof VOICE_RECORDING_FAILURE_REASONS)[keyof typeof VOICE_RECORDING_FAILURE_REASONS];

export interface VoiceRecordingFailure {
  readonly reason: VoiceRecordingFailureReason;
  readonly message: string | null;
  readonly isRetryable: boolean;
}

/**
 * `sinceMs` is a timeline position, not a duration: the recorder runs
 * continuously across a question so a cut names where the answer began.
 */
export interface VoiceAssetCutInput {
  readonly sinceMs?: number;
}

export interface VoiceRecordingPort {
  readonly status: VoiceRecordingStatus;
  /**
   * Normalised microphone loudness, 0–1, or `undefined` from a port that cannot
   * measure one. Absent means unknown, never speaking: a surface that animates
   * on level has to read it as silence, or a port without metering ripples
   * through a mute microphone for the whole session.
   */
  readonly audioLevel?: number;
  readonly subscribeAudioSamples?: (
    listener: (sample: {
      readonly audioLevel: number;
      readonly rawLevel: number;
      readonly sampledAt: number;
      readonly isRunning: boolean;
    }) => void
  ) => () => void;
  readonly failure: VoiceRecordingFailure | null;
  readonly start: (deviceId?: string) => Promise<void>;
  readonly stop: () => Promise<void>;
  readonly cutAsset: (
    input: VoiceAssetCutInput
  ) => Promise<ResponderMessageAsset | null>;
}
